import React, {Fragment, useEffect, useRef, useState} from "react";

import './fileUploader.scss';
import {Image} from "antd";
import {Link} from "react-router-dom";
import {set} from "react-hook-form";

// Constante con formatos de imagen permitidos
const DEFAULT_IMAGE_FORMATS = ["png", "webp", "jpeg", "jpg", "svg", "webp"];

export const FileUploader = ({
                                 onFilesSelected,
                                 validateDimensions= false,
                                 dimensions = {
                                     width: 0,
                                     height: 0
                                 },
                                 updateFiles = [],
                                 acceptFiles = [],
                                 multipleFiles = false,
                                 maxFiles = 1,
                                 fieldName = "",
                                 onDeleteFiles,
                                 maxSizeFile = 0
                             }) => {
    const [files, setFiles] = useState([]);
    const inputFileRef = useRef(null);
    const [allowedFormats, setAllowedFormats] = useState([]);
    const [dragStyle, setDragStyle] = useState("");
    // Se almacena el tamaño máximo en bytes
    const [maxSize, setMaxSize] = useState(maxSizeFile * 1024);

    // Actualiza los formatos permitidos a partir de la prop acceptFiles
    useEffect(() => {
        const formats = acceptFiles.map((type) => {
            const [main, sub] = type.split("/");
            return sub === "*" ? (main === "image" ? "Cualquier imagen" : `Cualquier ${main}`) : sub;
        });

        // Compara los arrays antes de actualizar
        if (JSON.stringify(formats) !== JSON.stringify(allowedFormats)) {
            setAllowedFormats(formats);
        }
    }, [acceptFiles]);

    // Actualiza el tamaño máximo si la prop maxSizeFile cambia
    useEffect(() => {
        setMaxSize(maxSizeFile * 1024);
    }, [maxSizeFile]);

    // Si se reciben archivos actualizados por props, se cargan
    useEffect(() => {
        if (updateFiles.length > 0) {
            setFiles(updateFiles.map(item => ({
                file: item,
                approve: true
            })));
        }
    }, [updateFiles]);

    const cleanInput = () => {
        if (inputFileRef.current) {
            inputFileRef.current.value = "";
        }
    };

    // Valida el tamaño del archivo (en KB)
    const isFileSizeValid = (file) => {
        const sizeInKB = file.size / 1024;
        return maxSize === 0 ? true : sizeInKB <= maxSize;
    };

    // Valida las dimensiones de una imagen
    const isImageDimensionsValid = (file) => {
        return new Promise((resolve) => {
            const imgURL = URL.createObjectURL(file);
            const img = new Image();
            img.src = imgURL;
            img.onload = () => {
                const valid = !(img.width > dimensions.width || img.height > dimensions.height);
                URL.revokeObjectURL(imgURL);
                resolve(valid);
            };
        });
    };

    // Función para validar un único archivo
    const validateFile = async (file) => {
        const fileType = file.type?.split("/")[1] || "";
        const sizeValid = isFileSizeValid(file);
        const dimensionsValid = validateDimensions ? await isImageDimensionsValid(file) : true;
        let typeValid = true;

        if (sizeValid && acceptFiles.length > 0) {
            // Aquí se puede ajustar la lógica según los requerimientos específicos
            if (
                (allowedFormats.includes(fileType) && !DEFAULT_IMAGE_FORMATS.includes(fileType)) ||
                allowedFormats.includes("video") ||
                allowedFormats.includes("audio")
            ) {
                typeValid = true;
            } else if (
                validateDimensions &&
                (allowedFormats.includes("image") || DEFAULT_IMAGE_FORMATS.includes(fileType))
            ) {
                typeValid = true;
            } else if (
                !validateDimensions &&
                (allowedFormats.includes("image") || DEFAULT_IMAGE_FORMATS.includes(fileType))
            ) {
                typeValid = true;
            }
        }
        return {
            file,
            approve: sizeValid && dimensionsValid && typeValid,
        };
    };

    const handleFiles = async (e) => {
        e.preventDefault();
        setDragStyle("");
        const selectedFiles = e.dataTransfer
            ? Array.from(e.dataTransfer.files)
            : Array.from(e.target.files);

        const validatedFiles = await Promise.all(selectedFiles.map(validateFile));

        if (multipleFiles) {
            const approvedFiles = files.filter((f) => f.approve).map((f) => f.file);
            // Solo se agregan archivos nuevos si no se excede el límite
            const newFiles = validatedFiles.filter(
                (item) => approvedFiles.length < maxFiles && item.approve
            );
            const updatedFiles = [...files, ...newFiles];
            setFiles(updatedFiles);
            onFilesSelected(updatedFiles.filter((f) => f.approve).map((f) => f.file));
        } else {
            setFiles(validatedFiles);
            if (validatedFiles[0].approve) onFilesSelected(validatedFiles[0].file);
        }
        cleanInput();
    };

    const handleRemoveFile = (index, fileItem) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        if (onDeleteFiles) {
            onDeleteFiles(newFiles.filter((item) => item.approve), fileItem);
        }
        if (newFiles.length === 0) cleanInput();
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        setDragStyle("-on-drag");
    };

    const handleGenerateLink = (item) => URL.createObjectURL(item);

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragStyle("");
    };

    // Se decide qué función usar en onDrop (para uno o varios archivos)
    const handleDrop = (e) => {
        if (files.length < maxFiles) handleFiles(e);
    };

    return (
        <section className={`file-uploader__container`}>
            <div
                className={`file-uploader__uploader ${files.length > 0 ? "active" : ""} ${dragStyle}`}
                onDrop={handleDrop}
                onDragOver={handleDragEnter}
                onDragLeave={handleDragLeave}
            >
                <div className="file-uploader__uploader__info">
                    {((!multipleFiles && files.length === 0) ||
                        (multipleFiles && files.length < maxFiles)) ? (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
                                <path
                                    d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128l-368 0zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39L296 392c0 13.3 10.7 24 24 24s24-10.7 24-24l0-134.1 39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"/>
                            </svg>
                            <div>
                                <p>
                                    <span>Arrastra y suelta</span> tus archivos aquí
                                </p>
                                {maxSize > 0 && <p>Límite de {maxSizeFile}MB por archivo.</p>}
                                {acceptFiles.length > 0 && (
                                    <p>
                                        Archivos permitidos:{" "}
                                        <span className="file-format">{allowedFormats.join(", ")}</span>
                                    </p>
                                )}
                                {validateDimensions && (
                                    <p>
                                        Dimensiones de imagen: {dimensions.width}x{dimensions.height}
                                    </p>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="check-container">
                                <div className="check-container__checkmark">
                                    <svg viewBox="0 0 65 51" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M7 25L27.3077 44L58.5 7"
                                            stroke="white"
                                            strokeWidth="13"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <div>
                                <p>Límite de archivos alcanzado</p>
                            </div>
                        </>
                    )}
                </div>

                <input
                    ref={inputFileRef}
                    type="file"
                    id={fieldName}
                    onChange={handleFiles}
                    accept={acceptFiles.join(",")}
                    name={fieldName}
                    multiple={multipleFiles}
                />

                {((multipleFiles && files.length < maxFiles) || (!multipleFiles && files.length === 0)) && (
                    <button onClick={() => inputFileRef.current.click()} type={'button'} className="btn-browser">
                        {multipleFiles ? "Buscar archivos" : "Buscar archivo"}
                    </button>
                )}

                {files.length > 0 && (
                    <div className="file-uploader__list">
                        {files.map((item, index) => {
                            const fileSize = bytesToSize(item.file.size)
                            const fileType = item.file.type?.split("/")[1] ?? '';

                            return (
                                <Fragment key={index}>
                                    <div className={`file-uploader__list-item ${item.approve ? 'success' : 'failed'}`}>
                                        <span>
                                          {item.approve ? (
                                              <svg className="success" xmlns="http://www.w3.org/2000/svg"
                                                   viewBox="0 0 512 512">
                                                  <path
                                                      d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/>
                                              </svg>
                                          ) : (
                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"
                                                   className="failed">
                                                  <path
                                                      d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z"/>
                                              </svg>
                                          )}
                                        </span>
                                        {DEFAULT_IMAGE_FORMATS.includes(fileType) ? (
                                            <Image width={35} src={URL.createObjectURL(item.file)}/>
                                        ) : (
                                            <span>
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                                  <path
                                                      d="M0 64C0 28.7 28.7 0 64 0H224V128c0 17.7 14.3 32 32 32H384V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zm384 64H256V0L384 128z"/>
                                                </svg>
                                            </span>
                                        )}
                                        <div className="file-info">
                                            <p className="name">{item.file?.name ?? item.file.fileName}</p>
                                            <p className="size">{fileSize}</p>
                                        </div>
                                        <div className="file-uploader__actions">
                                            {!DEFAULT_IMAGE_FORMATS.includes(fileType) && item.approve && (
                                                <Link
                                                    className="btn-download"
                                                    href={() => handleGenerateLink(item.file)}
                                                    target="_blank"
                                                    download={true}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                                        <path
                                                            d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 242.7-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7 288 32zM64 352c-35.3 0-64 28.7-64 64l0 32c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-32c0-35.3-28.7-64-64-64l-101.5 0-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352 64 352zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"/>
                                                    </svg>
                                                </Link>
                                            )}
                                            <button type="button" className="btn-trash"
                                                    onClick={() => handleRemoveFile(index, item)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                                    <path
                                                        d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </Fragment>
                            )
                        })}
                    </div>
                )}

                {multipleFiles && (
                    <p>
                        <strong>{files.filter((f) => f.approve).length} / {maxFiles} </strong>
                        {files.filter((f) => f.approve).length > 1 ? "archivos seleccionados" : "archivo seleccionado"}
                    </p>
                )}
            </div>
        </section>
    );
}

// Función de utilidad para convertir bytes a tamaño legible
const bytesToSize = (bytes, separator = "") => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "n/a";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return i === 0
        ? `${bytes}${separator}${sizes[i]}`
        : `${(bytes / 1024 ** i).toFixed(1)}${separator}${sizes[i]}`;
};